import { Fab, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Fade, IconButton, Icons, ScrollTag } from 'utoniq-ui'

import {
  ArtistThreadChannelFilterType,
  ArtistThreadChannelOrderByType,
  ArtistThreadChannelsOptionInput,
  Maybe,
} from '@/clients/utoniq-core/schema'
import {
  BaseLayout,
  BottomSafeArea,
  Head,
  SideMenu,
  SideMenuProps,
} from '@/components/layouts'
import {
  ThreadBox,
  ThreadBoxProps,
  ThreadBoxSkeleton,
} from '@/components/organisms'
import {
  FullscreenModal,
  ScrollFetcher,
} from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { config } from '@/config'
import { StorageImage, useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { usePrivateAction } from '@/lib/privateAction'

export * from './fragments'

export type State = {
  loading: boolean
  artistName: string
  refetching: boolean
  hasNextPage: boolean
  hasPatron: boolean
  isShowModalPersonalInfoRequire: boolean
  threads: Maybe<{ edges: { node: ThreadBoxProps['thread'] }[] }>
  artistId: string
  followArtists: Maybe<SideMenuProps['followArtists']>
  memberArtists: Maybe<SideMenuProps['memberArtists']>
  user: Maybe<SideMenuProps['user']>
  isLogin: boolean
  isArtist: Maybe<SideMenuProps['isArtist']>
  liveStreamId: string | undefined
  filter: ArtistThreadChannelsOptionInput
}

type ThreadTypeCellProps = {
  onFilter: (filter?: Maybe<ArtistThreadChannelFilterType>) => void
  filter: ArtistThreadChannelsOptionInput
}

type SelectButtonCellProps = {
  onOrderBy: (orderBy?: Maybe<ArtistThreadChannelOrderByType>) => void
  filter: ArtistThreadChannelsOptionInput
}

type ThreadListCellProps = {
  loading: boolean
  isShowModalPersonalInfoRequire: boolean
  hasNextPage: State['hasNextPage']
  threads: State['threads']
  hasPatron: State['hasPatron']
  onFetchMore: () => Promise<void>
}

type BaseCellProps = {
  artistId: State['artistId']
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: (options: ArtistThreadChannelsOptionInput) => Promise<void>
  onRefetch: (options: ArtistThreadChannelsOptionInput) => Promise<void>
}

export const ArtistThreadsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onRefetch,
}) => {
  const { t } = useTranslation('artistThreads')
  const { analytics } = useAnalytics()
  const { routerNames } = useRouter()
  const [open, setOpen] = useState<boolean>(false)
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)
  const [options, setOptions] = useState<ArtistThreadChannelsOptionInput>({
    filter: state.filter.filter,
    orderBy: state.filter.orderBy,
  })
  const params = new URLSearchParams(window.location.search)
  const tab = params.get('tab')

  const handleOrderBy = useCallback(
    (orderBy?: Maybe<ArtistThreadChannelOrderByType>) => {
      setOptions(prevState => {
        onRefetch({ ...prevState, orderBy })
        return { ...prevState, orderBy }
      })
    },
    [onRefetch]
  )

  const handleFilter = useCallback(
    (filter?: Maybe<ArtistThreadChannelFilterType>) => {
      setOptions(prevState => {
        onRefetch({ ...prevState, filter })
        return { ...prevState, filter }
      })
    },
    [onRefetch]
  )

  useEffect(() => {
    const UrlParams = `?tab=${options.filter ?? 'all'}&sort=${options.orderBy}`
    // React Routerのhistoryを使うとソート順を変えた際に、スクロール位置が一番上になるため、JavaScriptで直接historyを利用している
    window.history.replaceState(null, '', UrlParams)
  }, [options.orderBy, options.filter])

  useEffect(() => {
    const eventTab = tab === 'all' ? undefined : tab
    if (eventTab !== options.filter) {
      setOptions({
        filter: options.filter,
        orderBy: ArtistThreadChannelOrderByType.CreatedAt,
      })
    }
  }, [options.filter])

  return (
    <ArtistThemeProvider>
      <BaseLayout
        navigation={{
          type: 'artist',
          props: {
            isLive: !!state.liveStreamId,
            hasManagementTask: !!state.user?.hasManagementTask,
            unreadNotificationCount: state.user?.unreadNotificationCount,
          },
        }}
        header={{
          title: t('title'),
          right: (
            <IconButton.Help
              edge="end"
              color="primary"
              onClick={() => {
                analytics?.track('click_help', {
                  applicationKey: config.applicationKey,
                  artistId: state.artistId,
                })
                setOpen(true)
              }}
            />
          ),
          left: config.applicationType === 'organization' && (
            <IconButton.AvatarNoImage
              edge="start"
              onClick={() => setOpenSideMenu(!openSideMenu)}
              color="icon.line"
              bgcolor="icon.background"
              badge={
                state.user?.hasManagementTask ||
                !!state.user?.unreadNotificationCount
              }
            />
          ),
        }}
      >
        <Head title={[routerNames.ArtistThreads, state.artistName]} />

        {state.isLogin && (
          <ThreadTypeCell onFilter={handleFilter} filter={options} />
        )}
        <SelectButtonCell onOrderBy={handleOrderBy} filter={options} />
        <ThreadListCell
          loading={state.loading || state.refetching}
          hasNextPage={state.hasNextPage}
          hasPatron={state.hasPatron}
          threads={state.threads}
          isShowModalPersonalInfoRequire={state.isShowModalPersonalInfoRequire}
          onFetchMore={() => onFetchMore(options)}
        />
        <BaseCell artistId={state.artistId} />
        <ThreadHelpModal open={open} onClose={() => setOpen(false)} />
        <SideMenu
          followArtists={state.followArtists}
          memberArtists={state.memberArtists}
          open={openSideMenu}
          onClose={() => setOpenSideMenu(false)}
          user={state.user}
          isArtist={state.isArtist}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

export type FilterValue = ArtistThreadChannelFilterType | undefined
export const channelFilterTypes = [
  undefined,
  ArtistThreadChannelFilterType.MyThread,
  ArtistThreadChannelFilterType.MyComment,
  ArtistThreadChannelFilterType.Official,
  ArtistThreadChannelFilterType.TradeCall,
  ArtistThreadChannelFilterType.Request,
  ArtistThreadChannelFilterType.Event,
  ArtistThreadChannelFilterType.Faq,
]
const ThreadTypeCell: Cell<ThreadTypeCellProps> = ({ onFilter, filter }) => {
  const { t } = useTranslation('artistThreads')
  const [value, setValue] = useState<FilterValue>(filter.filter ?? undefined)

  const tags = useMemo<{ value: FilterValue; label: string }[]>(
    () =>
      channelFilterTypes.map(type => ({
        value: type,
        label: t(`category_${type ?? 'all'}`),
      })),
    [t]
  )

  const handleSelectType = useCallback(
    async (newValue: FilterValue) => {
      setValue(newValue)
      await onFilter(newValue)
    },
    [onFilter]
  )

  return (
    <CellFrame sx={{ px: 5 }}>
      <ScrollTag onClick={handleSelectType} value={value} tags={tags} />
    </CellFrame>
  )
}

const orderByTypes = [
  ArtistThreadChannelOrderByType.CreatedAt,
  ArtistThreadChannelOrderByType.Recommend,
  ArtistThreadChannelOrderByType.LikeCount,
  ArtistThreadChannelOrderByType.CommentCount,
]
const SelectButtonCell: Cell<SelectButtonCellProps> = ({
  onOrderBy,
  filter,
}) => {
  const { t } = useTranslation('artistThreads')
  const [options] = useState([
    t('newArrivalOrder'),
    t('recommendOrder'),
    t('likeOrder'),
    t('commentCountOrder'),
  ])
  const sortIndex = orderByTypes.findIndex(order => order === filter.orderBy)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedIndex, setSelectedIndex] = useState<number>(sortIndex)
  const open = Boolean(anchorEl)
  const params = new URLSearchParams(window.location.search)
  const tab = params.get('tab')

  const handleClickListItem = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleMenuItemClick = useCallback(
    (event: MouseEvent<HTMLElement>, index: number) => {
      setSelectedIndex(index)
      onOrderBy(orderByTypes[index])
      setAnchorEl(null)
    },
    [onOrderBy]
  )
  useEffect(() => {
    const eventTab = tab === 'all' ? undefined : tab
    if (eventTab !== filter.filter) {
      setSelectedIndex(0)
    }
  }, [filter.filter])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <CellFrame sx={{ px: 5 }}>
      <Stack direction="row-reverse">
        <Button
          variant="text"
          onClick={handleClickListItem}
          sx={{ mr: -1 }}
          endIcon={<Icons.DownArrow fontSize="xSmall" />}
        >
          <Typography variant="caption">{options[selectedIndex]}</Typography>
        </Button>
      </Stack>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {options.map((option, index) => (
          <MenuItem
            key={index}
            selected={index === selectedIndex}
            sx={{ minWidth: 200 }}
            onClick={event => handleMenuItemClick(event, index)}
          >
            <Typography variant="body2">{option}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </CellFrame>
  )
}

const ThreadListCell: Cell<ThreadListCellProps> = ({
  threads,
  loading,
  isShowModalPersonalInfoRequire,
  hasPatron,
  hasNextPage,
  onFetchMore,
}) => {
  const { t } = useTranslation('artistThreads')
  const { analytics } = useAnalytics()
  return (
    <CellFrame sx={{ px: 5 }}>
      <ScrollFetcher
        nodes={threads?.edges?.map(({ node }) => (
          <ThreadBox
            key={node.id}
            thread={node}
            hasPatron={hasPatron}
            isShowModalPersonalInfoRequire={isShowModalPersonalInfoRequire}
            onClick={() => {
              analytics?.track('click_select', {
                applicationKey: config.applicationKey,
                artistId: node.artistId,
                threadId: node.id,
                name: 'thread',
              })
            }}
          />
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        emptyMessage={t('threadListEmptyMessage')}
        loadingComponent={
          <Stack spacing={6} sx={{ py: 2 }}>
            {Array.from(Array(3).keys()).map(key => (
              <ThreadBoxSkeleton key={key} />
            ))}
          </Stack>
        }
      />
    </CellFrame>
  )
}

const BaseCell: Cell<BaseCellProps> = ({ artistId }) => {
  const { generatePath, routerPages } = useRouter()
  const { handleLoginModal } = usePrivateAction()
  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 72,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab
          size="medium"
          color="primary"
          onClick={handleLoginModal()}
          href={generatePath(routerPages.ArtistThreadCreate, {
            artistId,
          })}
        >
          <Icons.Pencil fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
    </Stack>
  )
}

type ThreadHelpModalProps = {
  open: boolean
  onClose: () => void
}
const ThreadHelpModal = ({ open, onClose }: ThreadHelpModalProps) => {
  const theme = config.theme.dark ? 'dark' : 'light'
  const { t } = useTranslation('artistThreads')

  return (
    <FullscreenModal open={open} onClose={onClose}>
      <Fade>
        <BaseLayout
          header={{
            title: t('howToThread'),
            left: <IconButton.Close edge="start" onClick={onClose} />,
          }}
        >
          <BottomSafeArea
            sx={{ bgcolor: 'background.default' }}
            defaultProps={{ sx: { bgcolor: 'grey.50' } }}
          >
            <StorageImage
              target="common"
              width="100%"
              src={`images/info/threadInfo--${theme}.png`}
            />
          </BottomSafeArea>
        </BaseLayout>
      </Fade>
    </FullscreenModal>
  )
}
